/**
 * Brings back the Judge.me "From our customers" reviews grid when it loads empty.
 *
 * The grid asks cdn.judge.me for its reviews. That CDN sometimes answers without the
 * CORS header (seen 2026-11-04 on the phone layout, which asks for 10 reviews), the
 * browser blocks the answer and keeps it cached for up to 20 minutes, and the app
 * then mounts a grid with no reviews: the whole block disappears from the page.
 *
 * When a grid ends up empty, this re-runs the app's own widget code on it:
 *  1. after fetching the reviews again past the browser cache;
 *  2. if that still fails, from the review data Judge.me already puts in the page
 *     (window.jdgm.data.reviewsGridWidget), which needs no network at all.
 * Nothing changes when the grid loads normally.
 */
(() => {
  if (window.Shopify && window.Shopify.designMode) return;

  const GRID = '.jdgm-reviews-grid-widget[data-entry-point]';
  const DATA_URL = 'cdn.judge.me/widgets/reviews_grid_widget_data';
  const MANAGER_FILE = /\/ReviewsGridWidgetManager-[\w-]+\.js$/;
  const CHECK_EVERY_MS = 500;
  const GIVE_UP_AFTER_MS = 20000;

  const resources = () => performance.getEntriesByType('resource').map((entry) => entry.name);

  // The app mounted its grid but drew nothing: only a Vue comment is left inside.
  const isEmpty = (grid) =>
    grid.classList.contains('jdgm-widget-revamp') && grid.__vue_app__ && !grid.firstElementChild;

  // The widget class lives in a hashed file next to reviews_grid.js; importing the URL the
  // app already loaded gives back the same module, so this follows Judge.me's updates.
  async function loadManager() {
    const url = resources().find((name) => MANAGER_FILE.test(name));
    if (!url) return null;
    const module = await import(url);
    return module.ReviewsGridWidgetManager || null;
  }

  // Replace the blocked copies in the browser cache. Same Accept header as the app,
  // because the CDN answer varies on it.
  function refetchReviews() {
    const urls = [...new Set(resources().filter((name) => name.includes(DATA_URL)))];
    return Promise.all(
      urls.map((url) =>
        fetch(url, { cache: 'reload', headers: { Accept: 'application/json' } }).catch(() => null)
      )
    );
  }

  // The app goes to the network when the in-page data has more than one page or when a
  // tag filter is on. The "featured" filter repeats the "featured reviews" selection (the
  // in-page featured list holds only featured reviews), so it can go; marking every
  // selection as a single page then makes the app draw from the in-page data.
  function useInPageReviews(grid) {
    const data = window.jdgm && window.jdgm.data && window.jdgm.data.reviewsGridWidget;
    if (!data) return;
    if (grid.dataset.reviewSelection === 'featured_reviews') grid.dataset.filterFeatured = 'false';
    ['all_reviews', 'featured_reviews', 'product_reviews', 'store_reviews'].forEach((key) => {
      const selection = data[key];
      if (!selection) return;
      selection.total_pages = 1;
      if (selection.media_only) selection.media_only.total_pages = 1;
    });
  }

  async function redraw(grid, Manager) {
    if (grid.__vue_app__) grid.__vue_app__.unmount();
    await new Manager(grid).initialize();
    return Boolean(grid.firstElementChild);
  }

  async function repair(grid) {
    try {
      const Manager = await loadManager();
      if (!Manager) return;
      await refetchReviews();
      if (await redraw(grid, Manager)) return;
      useInPageReviews(grid);
      await redraw(grid, Manager);
    } catch (error) {
      console.warn('Reviews grid retry failed:', error);
    }
  }

  // Wait for the app to finish; act only when the grid is still empty on two checks in a row.
  function watch(grid) {
    let emptyChecks = 0;
    const started = Date.now();
    const timer = setInterval(() => {
      emptyChecks = isEmpty(grid) ? emptyChecks + 1 : 0;
      if (emptyChecks >= 2) {
        clearInterval(timer);
        repair(grid);
      } else if (grid.firstElementChild || Date.now() - started > GIVE_UP_AFTER_MS) {
        clearInterval(timer);
      }
    }, CHECK_EVERY_MS);
  }

  function start() {
    document.querySelectorAll(GRID).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
